import { apiClient } from './client'
import {
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
  clearAuthTokens,
} from '@/config/auth'

// Login
export interface LoginRequest {
  email: string
  password: string
  rememberMe: boolean
}

export interface LoginResponse {
  accessToken: string
  refreshToken: string
  user: {
    id: string
    email: string
    name: string | null
    role: string
    clinicIds: string[]
    doctorId: string | null
    isActive: boolean
    mustChangePassword: boolean
  }
}

// Validate
export interface ValidateUserResponse {
  valid: boolean
  user?: {
    id: string
    email: string
    name: string | null
    isActive: boolean
    mustChangePassword: boolean
    role: string
    clinics: Array<{
      clinicId: string
      name: string
    }>
    doctor: {
      id: string
      name: string
      specialty: string
    } | null
  }
}

// User Profile
export interface UserProfile {
  user: {
    id: string
    email: string
    name: string | null
    role: string
    clinicIds: string[]
    doctorId: string | null
    mustChangePassword: boolean
  }
}

// Access Profile
export interface AccessProfile {
  profile: {
    user: {
      id: string
      email: string
      name: string | null
      role: string
    }
    accessibleClinics: any[]
    accessibleDoctors: any[]
  }
}

// User Status
export interface UserStatus {
  status: {
    isRegistered: boolean
    hasRole: boolean
    hasClinics: boolean
    isFullyConfigured: boolean
    needsApproval: boolean
    role: string
    clinicIds: string[]
  }
}

// Set Role
export interface SetUserRoleRequest {
  userId: string
  role: string
}

// Refresh Token
export interface RefreshTokenResponse {
  accessToken: string
}

// Change Password
export interface ChangePasswordRequest {
  oldPassword: string
  newPassword: string
}

// Reset Password (Admin)
export interface ResetPasswordResponse {
  success: boolean
  temporaryPassword: string
  message: string
}

/**
 * Login with email and password
 */
export async function login(data: LoginRequest): Promise<LoginResponse> {
  const response = await apiClient.post<LoginResponse>('auth/login', data)

  // Store tokens in localStorage
  setAccessToken(response.data.accessToken)
  setRefreshToken(response.data.refreshToken)

  return response.data
}

/**
 * Refresh access token using refresh token
 * Note: Timeout is managed by the apiClient interceptor (10 seconds)
 */
export async function refreshAccessToken(): Promise<string> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    throw new Error('No refresh token available')
  }

  const response = await apiClient.post<RefreshTokenResponse>(
    'auth/refresh',
    { refreshToken },
    { timeout: 8000 } // 8 second timeout for refresh requests
  )

  // Update access token
  setAccessToken(response.data.accessToken)

  return response.data.accessToken
}

/**
 * Logout
 */
export async function logout(): Promise<void> {
  try {
    await apiClient.post('auth/logout')
  } catch (error) {
    // Ignore errors on logout
    console.error('Logout error:', error)
  } finally {
    // Always clear tokens
    clearAuthTokens()
  }
}

/**
 * Change password (for current user)
 */
export async function changePassword(
  oldPassword: string,
  newPassword: string
): Promise<void> {
  await apiClient.post('auth/change-password', { oldPassword, newPassword })
}

/**
 * Reset user password (admin only)
 */
export async function resetUserPassword(userId: string): Promise<ResetPasswordResponse> {
  const response = await apiClient.put<ResetPasswordResponse>(
    `auth/users/${userId}/reset-password`
  )
  return response.data
}

/**
 * Validate current user and fetch profile
 */
export async function validateUser(): Promise<ValidateUserResponse> {
  const response = await apiClient.post<ValidateUserResponse>('auth/validate')
  return response.data
}

/**
 * Get current user profile
 */
export async function getUserProfile(): Promise<UserProfile> {
  const response = await apiClient.get<UserProfile>('auth/profile')
  return response.data
}

/**
 * Get current user status (has roles, organization, needs approval, etc)
 */
export async function getUserStatus(): Promise<UserStatus> {
  const response = await apiClient.get<UserStatus>('auth/status')
  return response.data
}

/**
 * Set user roles (admin only)
 */
export async function setUserRole(data: SetUserRoleRequest): Promise<{ success: boolean }> {
  const response = await apiClient.post<{ success: boolean }>('auth/roles', data)
  return response.data
}

/**
 * Get user access profile (clinics and doctors)
 */
export async function getAccessProfile(): Promise<AccessProfile> {
  const response = await apiClient.get<AccessProfile>('auth/profile/access')
  return response.data
}

export const authService = {
  login,
  logout,
  refreshAccessToken,
  changePassword,
  resetUserPassword,
  validateUser,
  getUserProfile,
  getUserStatus,
  setUserRole,
  getAccessProfile,
}

export default authService
